import React, { useState, useEffect } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { FaFileInvoice } from "react-icons/fa";

import "./GoldCollectionReport.css";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const GoldCollectionReport = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.userProfile);
  const navigate = useNavigate();

  const [centers, setCenters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");

  const [collectionData, setCollectionData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  useEffect(() => {
    if (!profile?.id) return;

    const fetchCenters = async () => {
      try {
        const res = await axios.get(
          `https://narrowitsolution.in/api/group/centre?LoanofficerId=${profile.id}`
        );
        setCenters(res.data?.data || []);
      } catch (err) {
        console.error("Center fetch error:", err);
      }
    };

    fetchCenters();
  }, [profile?.id]);

  useEffect(() => {
    if (!selectedCenter) return;

    const fetchGroups = async () => {
      try {
        const res = await axios.get(
          `https://narrowitsolution.in/api/groupupdate/getall?LoanofficerId=${profile?.id}&CenterId=${selectedCenter}`
        );
        setGroups(res.data?.data || []);
      } catch (err) {
        console.error("Group fetch error:", err);
      }
    };

    fetchGroups();
  }, [selectedCenter, profile?.id]);

  const fetchCollection = async (page = 1) => {
    if (!profile?.branchId) return;

    try {
      setLoading(true);

      const res = await axios.get(
        "https://narrowitsolution.in/api/group-loan/emi/collection-report",
        {
          params: {
            superAdminId: profile.superAdminId,
            centerId: selectedCenter || undefined,
            groupId: selectedGroup || undefined,
            page,
            limit: itemsPerPage,
          },
        }
      );

      if (res.data?.success) {
        setCollectionData(res.data.data || []);
        setCurrentPage(res.data.pagination.currentPage);
        setTotalPages(res.data.pagination.totalPages);
        setTotalRecords(res.data.pagination.totalRecords);
      }
    } catch (err) {
      console.error("Collection fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedCenter || !selectedGroup) return;
    fetchCollection(currentPage);
  }, [currentPage, itemsPerPage]);

  const filteredData = collectionData.filter((r) => {
    const text = `
      ${r.loanAccount?.loanAccountNumber}
      ${r.loanAccount?.memberName}
      ${r.loanAccount?.centerName}
      ${r.loanAccount?.groupName}
    `.toLowerCase();

    return text.includes(searchQuery.toLowerCase());
  });

  const handleFilter = () => {
    if (!selectedCenter || !selectedGroup) {
      toast.warn("Please select center and group");
      return;
    }
    setCurrentPage(1);
    fetchCollection(1);
  };

  const exportCSV = () => {
    const csv = collectionData
      .map((r) =>
        [r.memberId, r.groupId, r.emiAmount, r.paidAmount, r.paidDate].join(",")
      )
      .join("\n");

    saveAs(new Blob([csv]), "gold_collection_report.csv");
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(collectionData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Gold Collection Report");
    XLSX.writeFile(wb, "gold_collection_report.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    autoTable(doc, {
      head: [["Member Id", "Group Id", "EMI Amount","Paid Amount", "Paid Date", "Status"]],
      body: collectionData.map((r) => [
        r.memberId,
        r.groupId,
        r.emiAmount,
        r.paidAmount,
        r.paidDate ? new Date(r.paidDate).toLocaleDateString() : "-",
        r.status,
      ]),
    });
    doc.save("gold_collection_report.pdf");
  };

  return (
    <div className="goldcr-container">
      <h1 className="goldcr-heading">Gold Collection Report</h1>

      <div className="goldcr-filterBox">
        <div className="goldcr-filter-wrapper">
          <div className="goldcr-filter-item">
            <div className="goldcr-filter">
              <label>Select Center</label>
              <select
                value={selectedCenter}
                onChange={(e) => setSelectedCenter(e.target.value)}
              >
                <option value="">Select Center</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.CenterName}
                  </option>
                ))}
              </select>
            </div>

            <div className="goldcr-filter">
              <label>Select Group</label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
              >
                <option value="">Select Group</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.groupname}, {g.groupcode}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="goldcr-filter-submit-btn">
            <button className="submit-btn" onClick={handleFilter}>
              Submit
            </button>
          </div>
        </div>
      </div>

      {filteredData.length > 0 && (
        <div className="goldcr-pdf-download-btns">
          <button onClick={exportCSV}><Download size={15} /> CSV</button>
          <button onClick={exportExcel}><Download size={15} /> Excel</button>
          <button onClick={exportPDF}><Download size={15} /> PDF</button>
        </div>
      )}

      <div className="goldcr-report-filter">
        <input
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
        <div>
          <label>Show </label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {ITEMS_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
            <option value="50000"> ALL</option>
          </select>
          <span> entries</span>
        </div>
      </div>

      <div className="goldcr-report-table-container">
        <table className="goldcr-report-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Member Id</th>
              <th>Group Id</th>
              <th>EMI Amount</th>
              <th>Paid Amount</th>
              <th>Paid Date</th>
              <th>Status</th>
              <th>Receipt</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" align="center">Loading...</td>
              </tr>
            ) : filteredData.length ? (
              filteredData.map((r, i) => {
                const nextemi = i < filteredData.length - 1 ? filteredData[i + 1] : null;

                return (
                  <tr key={r.id}>
                    <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                    <td>{r.memberId}</td>
                    <td>{r.groupId}</td>
                    <td>₹{r.emiAmount}</td>
                    <td>₹{r.paidAmount}</td>
                    <td>
                      {r.paidDate
                        ? new Date(r.paidDate).toLocaleDateString("en-GB")
                        : "-"}
                    </td>
                    <td className={r.status === "PAID" ? "status-paid" : ""}>
                      {r.status}
                    </td>

                    <td
                      className="receipt-cell"
                      onClick={() =>
                        navigate("/GoldLoan/GoldLoanCollectionReceipt", {
                          state: { r, nextemi },
                        })
                      }
                    >
                      <FaFileInvoice className="receipt-icon" />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" align="center">No data found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="goldcr-pagination">
        <button
          disabled={currentPage === 1 || filteredData.length === 0}
          onClick={() => setCurrentPage((p) => p - 1)}
        >
          ⬅ Prev
        </button>

        <span>
          {filteredData.length === 0
            ? "No data"
            : `Page ${currentPage} of ${totalPages}`}
        </span>

        <button
          disabled={currentPage === totalPages || filteredData.length === 0}
          onClick={() => setCurrentPage((p) => p + 1)}
        >
          Next ➡
        </button>
      </div>
    </div>
  );
};

export default GoldCollectionReport;
